import { Box, Collapse } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';

import AttachmentList from '../../../../shared/components/AttachmentList';
import { Attachment } from '../../../../types/work';
import { 
    Container as DescriptionContainer, 
    Content as DescriptionContent, 
    Title as DescriptionTitle } from '../Description/styled';
import { Caret, ItemContainer, ItemHeader, ItemTitle, LatestBadge } from './styled';

type FeedbackEntry = {
    text: string;
    iteration: number;
    feedback_type: string;
    attachments?: Attachment[];
};

type Props = {
    history: FeedbackEntry[];
    title?: string;
};

const getFeedbackSourceLabel = (t: string) => {
    const key = (t || '').toUpperCase();
    if (key === 'REQUEST_REVIEW') {
        return 'Client Feedback';
    }
    if (key === 'QUEST_REVIEW') {
        return 'Internal Feedback';
    }
    if (key === 'AI_QUEST_REVIEW') {
        return 'AI Feedback';
    }
    return 'Feedback';
};

const FeedbackHistory: FC<Props> = ({ history, title = 'Feedback history' }) => {
    
    const sorted = useMemo(
        () => [ ...history ].sort((a, b) => (b.iteration ?? 0) - (a.iteration ?? 0)),
        [ history ]
    );

    // Get attachments for each feedback entry
    const entryAttachments = useMemo(() => {
        return sorted.map(entry => {
            if (!entry.attachments || !Array.isArray(entry.attachments) || entry.attachments.length === 0) {
                return [];
            }
            return entry.attachments.map((att: Attachment) => ({
                id: att.id,
                name: att.name,
                type: att.type,
                step: att.step
            }));
        });
    }, [ sorted ]);

    const [ open, setOpen ] = useState<boolean[]>([]);
    useEffect(() => {
        setOpen(sorted.map((_, i) => i === 0));
    }, [ sorted ]);

    const toggle = (idx: number) => {
        setOpen(prev => prev.map((v, i) => (i === idx ? !v : v)));
    };

    return (
        <DescriptionContainer>
            <div style={{ flexGrow: 100 }}>
                <DescriptionTitle>{title}</DescriptionTitle>
                <Box height={12} />
                <DescriptionContent>This quest was returned for another iteration. Please make sure you address the latest feedback. </DescriptionContent>
                <Box height={12} />
                <Box display="flex" flexDirection="column" gap="8px">
                    {sorted.map((entry, idx) => {
                        const header = `Iteration ${entry.iteration} — ${getFeedbackSourceLabel(entry.feedback_type)}`;
                        const attachments = entryAttachments[idx] || [];
                        
                        return (
                            <ItemContainer key={`${entry.iteration}-${idx}`}>
                                <ItemHeader onClick={() => toggle(idx)}>
                                    <ItemTitle>
                                        {header}
                                        {idx === 0 && (
                                            <LatestBadge>Latest</LatestBadge>
                                        )}
                                    </ItemTitle>
                                    <Caret>{open[idx] ? '▾' : '▸'}</Caret>
                                </ItemHeader>
                                <Collapse in={open[idx]} timeout="auto" unmountOnExit>
                                    <Box mt={1}>
                                        <DescriptionContent>{entry.text}</DescriptionContent>
                                        <AttachmentList attachments={attachments} />
                                    </Box>
                                </Collapse>
                            </ItemContainer>
                        );
                    })}
                </Box>
            </div>
        </DescriptionContainer>
    );
};

export default FeedbackHistory;
